import { useState, useEffect, useRef } from 'react';
import { mediaUrl } from '../utils/media';
import Navbar from '../components/common/Navbar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, BookOpenIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TABS = ['Ebook Saya', 'Tambah/Edit', 'Pesanan', 'Grafik'];

const STATUS_COLOR = {
  paid: 'text-green-600',
  pending: 'text-yellow-600',
  failed: 'text-red-500',
  expired: 'text-gray-400',
};

export default function SellerDashboard() {
  const [tab, setTab] = useState(0);
  const [ebooks, setEbooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', price: '', category: '', discount_pct: '', affiliate_enabled: false, commission_type: 'percent', commission_value: '' });
  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const coverRef = useRef();
  const pdfRef = useRef();

  const fetchEbooks = () =>
    api.get('/api/seller/ebooks').then((r) => setEbooks(r.data)).catch(() => toast.error('Gagal memuat ebook'));

  const fetchOrders = () =>
    api.get('/api/seller/orders').then((r) => setOrders(r.data)).catch(() => toast.error('Gagal memuat pesanan'));

  useEffect(() => { fetchEbooks(); fetchOrders(); }, []);

  useEffect(() => {
    const monthlyData = orders
      .filter(o => o.status === 'paid')
      .reduce((acc, o) => {
        const d = new Date(o.created_at);
        const month = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        acc[month] = (acc[month] || 0) + Number(o.amount || 0);
        return acc;
      }, {});
    setChartData(Object.entries(monthlyData).map(([month, total]) => ({ month, total })));
  }, [orders]);

  const resetForm = () => {
    setForm({ title: '', description: '', price: '', category: '', discount_pct: '', affiliate_enabled: false, commission_type: 'percent', commission_value: '' });
    setCoverFile(null);
    setPdfFile(null);
    setEditing(null);
    if (coverRef.current) coverRef.current.value = '';
    if (pdfRef.current) pdfRef.current.value = '';
  };

  const startEdit = (ebook) => {
    setEditing(ebook);
    setForm({ title: ebook.title, description: ebook.description || '', price: ebook.price, category: ebook.category || '', discount_pct: ebook.discount_pct || '', affiliate_enabled: !!ebook.affiliate_enabled, commission_type: ebook.commission_type || 'percent', commission_value: ebook.commission_value || '' });
    setCoverFile(null);
    setPdfFile(null);
    setTab(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) fd.append(k, v); });
      if (coverFile) fd.append('cover', coverFile);
      if (pdfFile) fd.append('file', pdfFile);

      if (editing) {
        await api.put(`/api/seller/ebooks/${editing.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Ebook diperbarui');
      } else {
        if (!pdfFile) { toast.error('PDF wajib diunggah'); setLoading(false); return; }
        if (!coverFile) { toast.error('Cover wajib diunggah'); setLoading(false); return; }
        await api.post('/api/seller/ebooks', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Ebook berhasil diunggah');
      }
      resetForm();
      fetchEbooks();
      setTab(0);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Gagal menyimpan ebook'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus ebook ini?')) return;
    try {
      await api.delete(`/api/seller/ebooks/${id}`);
      toast.success('Ebook dihapus');
      fetchEbooks();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Gagal menghapus'));
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-16 md:pb-0">
      <Navbar title="Dashboard Penjual" />

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`py-3 px-4 text-sm font-medium whitespace-nowrap border-b-2 transition ${
              tab === i ? 'border-wa-dark text-wa-dark' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Tab 0: Ebook Saya */}
        {tab === 0 && (
          <div className="p-4 space-y-3">
            <button
              onClick={() => { resetForm(); setTab(1); }}
              className="flex items-center gap-2 bg-wa-dark text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-green-900 transition"
            >
              <PlusIcon className="w-4 h-4" /> Tambah Ebook
            </button>
            {ebooks.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Belum ada ebook.</div>
            ) : (
              ebooks.map(eb => (
                <div key={eb.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3">
                  {eb.cover_url ? (
                    <img src={mediaUrl(eb.cover_url)} alt={eb.title} className="w-14 h-14 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded bg-wa-light flex items-center justify-center flex-shrink-0">
                      <BookOpenIcon className="w-7 h-7 text-wa-dark" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{eb.title}</p>
                    <p className="text-xs text-gray-500">{eb.category || '-'}</p>
                    <div className="flex items-center gap-1.5">
                      {eb.discount_pct > 0 ? (
                        <>
                          <span className="text-sm font-semibold text-wa-dark">Rp {Number(eb.final_price || eb.price).toLocaleString('id-ID')}</span>
                          <span className="text-xs text-gray-400 line-through">Rp {Number(eb.price).toLocaleString('id-ID')}</span>
                          <span className="text-[10px] bg-red-500 text-white px-1 py-0.5 rounded font-bold">-{eb.discount_pct}%</span>
                        </>
                      ) : (
                        <span className="text-sm font-semibold text-wa-dark">
                          {eb.price === 0 ? 'Gratis' : `Rp ${Number(eb.price).toLocaleString('id-ID')}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(eb)} className="text-gray-400 hover:text-wa-dark transition">
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(eb.id)} className="text-gray-400 hover:text-red-500 transition">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab 1: Tambah/Edit */}
        {tab === 1 && (
          <div className="p-4 max-w-lg mx-auto">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">{editing ? 'Edit Ebook' : 'Tambah Ebook Baru'}</h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Judul ebook"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-wa-dark"
              />
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Deskripsi"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-wa-dark resize-none"
              />
              <input
                value={form.price}
                onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                placeholder="Harga (0 = gratis)"
                type="number"
                min="0"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-wa-dark"
              />
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Diskon (%) — opsional</label>
                <input
                  value={form.discount_pct}
                  onChange={e => setForm(p => ({ ...p, discount_pct: e.target.value }))}
                  placeholder="Contoh: 20 = diskon 20%"
                  type="number"
                  min="0"
                  max="100"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-wa-dark"
                />
                {Number(form.discount_pct) > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    Harga jual: <span className="line-through text-gray-400">Rp {Number(form.price || 0).toLocaleString('id-ID')}</span>{' '}
                    → Rp {Number(form.price * (1 - form.discount_pct / 100) || 0).toLocaleString('id-ID')}
                  </p>
                )}
              </div>

              {/* ── Affiliate ── */}
              <div className="border border-gray-100 rounded-xl p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.affiliate_enabled}
                    onChange={e => setForm(p => ({ ...p, affiliate_enabled: e.target.checked }))}
                    className="rounded"
                  />
                  Aktifkan Komisi Affiliate
                </label>
                {form.affiliate_enabled && (
                  <div className="flex gap-2">
                    <select
                      value={form.commission_type}
                      onChange={e => setForm(p => ({ ...p, commission_type: e.target.value }))}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-wa-dark"
                    >
                      <option value="percent">Persen (%)</option>
                      <option value="nominal">Nominal (Rp)</option>
                    </select>
                    <input
                      value={form.commission_value}
                      onChange={e => setForm(p => ({ ...p, commission_value: e.target.value }))}
                      placeholder={form.commission_type === 'percent' ? 'Contoh: 10' : 'Contoh: 5000'}
                      type="number"
                      min="0"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-wa-dark"
                    />
                    <span className="self-center text-sm text-gray-500">
                      {form.commission_type === 'percent' ? '%' : 'Rp'}
                    </span>
                  </div>
                )}
              </div>
              <input
                value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                placeholder="Kategori"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-wa-dark"
              />
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Cover {!editing && '(wajib)'}</label>
                {editing && editing.cover_url && (
                  <div className="mb-2">
                    <img src={mediaUrl(editing.cover_url)} alt="Current" className="w-24 h-32 object-cover rounded border" />
                    <p className="text-xs text-gray-400 mt-1">Cover saat ini</p>
                  </div>
                )}
                <input ref={coverRef} type="file" accept="image/*" onChange={e => setCoverFile(e.target.files[0])}
                  className="text-sm text-gray-600" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">File PDF {!editing && '(wajib)'}</label>
                {editing && editing.file_url && (
                  <p className="text-xs text-gray-500 mb-1">PDF: {editing.file_url.split('/').pop()}</p>
                )}
                <input ref={pdfRef} type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files[0])}
                  className="text-sm text-gray-600" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { resetForm(); setTab(0); }}
                  className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-full text-sm font-medium hover:bg-gray-50 transition">
                  Batal
                </button>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-wa-dark text-white py-2.5 rounded-full text-sm font-medium hover:bg-green-900 transition disabled:opacity-50">
                  {loading ? 'Menyimpan...' : editing ? 'Simpan Perubahan' : 'Unggah Ebook'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab 2: Pesanan */}
        {tab === 2 && (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ClipboardDocumentListIcon className="w-5 h-5" />
              <span className="font-medium text-sm">{orders.length} pesanan</span>
            </div>
            {orders.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Belum ada pesanan.</div>
            ) : (
              orders.map(o => (
                <div key={o.id} className="bg-white rounded-xl shadow-sm p-4 text-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-wa-text">{o.ebook_title}</p>
                      <p className="text-gray-500 text-xs">Pembeli: {o.buyer_name || o.buyer_email}</p>
                      <p className="text-gray-400 text-xs font-mono mt-0.5">#{o.id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-wa-dark">Rp {Number(o.amount).toLocaleString('id-ID')}</p>
                      <p className={`text-xs font-medium mt-0.5 ${STATUS_COLOR[o.status] || 'text-gray-500'}`}>
                        {o.status}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab 3: Grafik */}
        {tab === 3 && (
          <div className="p-4">
            <h3 className="font-semibold text-gray-700 mb-4">Pendapatan per Bulan</h3>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-2xl font-bold text-wa-dark mb-1">
                Rp {orders.filter(o => o.status === 'paid').reduce((s, o) => s + Number(o.amount || 0), 0).toLocaleString('id-ID')}
              </p>
              <p className="text-sm text-gray-500 mb-4">Total Pendapatan</p>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => `Rp ${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `Rp ${Number(v).toLocaleString('id-ID')}`} />
                  <Bar dataKey="total" fill="#075E54" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
